import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatBalanceWithCommas, formatCryptoPrice, formatPercentage, formatValueWithCompactNumber } from '@/lib/format';
import { cn, genJudgingSizeColor } from '@/lib/utils';
import tokenApiService from '@/request/token/token.api.service';
import NoData from '@/widget/NoData';
import TokenAvatar from '@/widget/TokenAvatar';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HoldingRowSkeleton from './HoldingRowSkeleton';
import smartMoneyApiService from '@/request/smartMoney/smartMoney.api.service';
import { useNavigate } from 'react-router-dom';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { useTradeStore } from '@/page/trade/store';
import { useAppStore } from '@/store';
import { useWsHoldingUpdate } from '@/page/trade/hook/usWsHoldingUpdate';
import { IAddressHolding } from '@/request/smartMoney/smartMoney.response.interface';
import { IHoldingToken } from '@/request/token/token.response.interface';

export interface IHoldingPriceBalance {
  unrealizedPnl: string | number;
  unrealizedRoi: string | number;
  tokenAddress: string;
  amount: string | number;
  symbol: string;
  balance: string | number;
  price: string | number;
  priceChg: string | number;
  avgBuyPrice: string | number;
  leftCost: string | number;
}

export default () => {
  const navigate = useNavigate();
  const { publish, subscribe } = useAppEventBusContext();
  const { walletAddress, chain } = useBoomWallet();
  const { chainInfo } = useAppStore();
  const { t } = useTranslation();
  const { tokenInfo, chainTokenAmount, tokenRealtimePrice, priceChg } = useTradeStore();
  const { tokenTradeInfo, setTokenTradeInfo } = useWsHoldingUpdate();

  const [renderList, setRenderList] = useState<IHoldingPriceBalance[]>([]);
  const [holdingList, setHoldingList] = useState<IHoldingPriceBalance[]>([]);
  const [loading, setLoading] = useState(false);

  // 更新目标代币的价格和收益信息
  const updateTargetTokenInfo = useCallback(
    (list: IHoldingPriceBalance[]) => {
      if (!list.length || !tokenRealtimePrice) return list;

      const newHoldingList = list.map((item) => {
        const isTargetToken = item.tokenAddress.toLowerCase() === tokenInfo?.address.toLowerCase();
        if (!isTargetToken) return item;

        if (BigNumber(chainTokenAmount).eq(0)) {
          return null;
        }

        const currentUSD = BigNumber(item.amount).times(tokenRealtimePrice);
        const unrealizedPnl = BigNumber(tokenRealtimePrice)
          .minus(tokenTradeInfo?.avgBuyPrice || 0)
          .times(item.amount)
          .toString();

        const unrealizedRoi = BigNumber(unrealizedPnl).div(item?.leftCost).toString();

        publish(SubscribeEvent.Update_Profit, {
          unrealizedPnl,
          unrealizedRoi,
        });

        return {
          ...item,
          amount: Number(BigNumber(chainTokenAmount).toFixed(4)).toString(),
          price: tokenRealtimePrice,
          priceChg,
          balance: currentUSD.toString(),
          unrealizedRoi,
          unrealizedPnl,
        };
      });

      return newHoldingList.filter(Boolean) as IHoldingPriceBalance[];
    },
    [tokenRealtimePrice, chainTokenAmount, tokenInfo?.address, priceChg, publish, tokenTradeInfo],
  );

  // 处理持仓数据转换
  const transformHoldingData = useCallback(
    (holdingItem: IAddressHolding, priceData: IHoldingToken[]): IHoldingPriceBalance | null => {
      try {
        const holding = priceData?.find((token: any) => token.address.toLowerCase() === holdingItem.tokenAddress.toLowerCase());

        const amount = BigNumber(holdingItem.totalBuyAmount).minus(holdingItem.totalSellAmount);
        const balance = amount.times(holdingItem.curPrice).toString();

        return {
          avgBuyPrice: holdingItem.avgBuyPrice,
          unrealizedPnl: holdingItem.unRealizedPnL.toString(),
          unrealizedRoi: holdingItem.unRealizedRoi.toString(),
          tokenAddress: holdingItem.tokenAddress,
          balance: balance.toString(),
          symbol: holdingItem.symbol,
          amount: amount.toString(),
          price: holdingItem.curPrice,
          leftCost: holdingItem.leftCost,
          priceChg: holding ? holding.priceChange24h : 0,
        };
      } catch (error) {
        console.error('Transform holding data error:', error);
        return null;
      }
    },
    [setTokenTradeInfo],
  );

  // 获取有效代币列表
  const getEffectiveTokens = (holdingList: IAddressHolding[]) => {
    const effectiveTokens = holdingList.filter((item) => BigNumber(item.totalBuyAmount).minus(item.totalSellAmount).gt(0.01));

    if (Number(chainTokenAmount) > 0 && tokenInfo?.address) {
      const targetToken = holdingList.find((item) => item.tokenAddress.toLowerCase() === tokenInfo?.address.toLowerCase());

      if (targetToken && !effectiveTokens.some((item) => item.tokenAddress.toLowerCase() === targetToken.tokenAddress.toLowerCase())) {
        effectiveTokens.push(targetToken);
      }
    }

    return effectiveTokens;
  };

  // 获取持仓数据
  const fetchHoldingData = useCallback(async () => {
    if (!walletAddress || !tokenInfo?.address) return;
    try {
      setLoading(true);
      const list = await smartMoneyApiService.getAddressHolding({
        address: walletAddress,
        chain: chain === 'bsc' ? 'bsc' : 'sol',
      });
      if (list.length === 0) return;

      const targetToken = list.find((item) => item.tokenAddress.toLowerCase() === tokenInfo?.address.toLowerCase());
      if (targetToken) {
        setTokenTradeInfo({
          totalBuyAmount: targetToken.leftAmount,
          totalBuyCost: targetToken.curRoundInfo.totalBuyVolume,
          curPirce: targetToken.curRoundInfo.avgBuyPrice,
          avgBuyPrice: targetToken.curRoundInfo.avgBuyPrice,
        });
      }

      const effectiveTokens = getEffectiveTokens(list);
      const effectiveAddressList = effectiveTokens.map((item) => item.tokenAddress);

      if (effectiveAddressList.length) {
        const priceRes = await tokenApiService.getTokenPriceList(effectiveAddressList);
        if (priceRes.code === 0) {
          const list = effectiveTokens.map((item) => transformHoldingData(item, priceRes.data)).filter(Boolean) as IHoldingPriceBalance[];
          setHoldingList(list);
        }
      } else {
        setHoldingList([]);
      }
    } catch (error) {
      setHoldingList([]);
    }
  }, [walletAddress, tokenInfo?.address, chainTokenAmount, chain, transformHoldingData]);

  // 更新渲染列表
  useEffect(() => {
    const updatedList = updateTargetTokenInfo(holdingList);
    setRenderList(updatedList);
    setLoading(false);
  }, [holdingList, updateTargetTokenInfo]);

  useEffect(() => {
    fetchHoldingData();
    const unSubscribe = subscribe(SubscribeEvent.Refresh_Holding, () => {
      fetchHoldingData();
    });
    return () => unSubscribe();
  }, []);

  return (
    <div>
      <Table className="w-full" wrapperClassName="text-[12px]">
        <TableHeader>
          <TableRow className="border-none text-[12px]">
            <TableHead className="w-[160px] px-0">{t('Account.price')}</TableHead>
            <TableHead className="text-right px-0">{t('Account.unrealizedProfit')}</TableHead>
            <TableHead className="text-right px-0">{t('Account.balance')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <HoldingRowSkeleton />
          ) : renderList.length > 0 ? (
            renderList.map((token) => {
              return (
                <TableRow
                  className="border-borderColor hover:!bg-[unset]"
                  key={token.tokenAddress}
                  onClick={() => navigate(`/${chainInfo.path}/trade/${token.tokenAddress}`)}
                >
                  <TableCell className="px-0">
                    <div className="flex items-center gap-x-[8px]">
                      <TokenAvatar logo="" address={token.tokenAddress} className="w-[30px] h-[30px]" />
                      <div>
                        <p>{token.symbol}</p>
                        <p>
                          <span>{formatCryptoPrice(token.price)}</span>&nbsp;·&nbsp;
                          <span className={cn(genJudgingSizeColor(token.priceChg))}>
                            {formatPercentage(token.priceChg, { showPositiveSign: true, scale: true })}
                          </span>
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-0">
                    <div className="text-12px text-right">
                      <p className={genJudgingSizeColor(token.unrealizedPnl)}>{formatBalanceWithCommas(token.unrealizedPnl)}</p>
                      <p className={cn('text-secondaryTextColor', genJudgingSizeColor(token.unrealizedRoi))}>
                        {formatPercentage(token.unrealizedRoi, { scale: true })}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right px-0">
                    <p>{formatValueWithCompactNumber(token.amount)}</p>
                    <p>{formatBalanceWithCommas(token.balance)}</p>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow className="bg-[unset]">
              <TableCell colSpan={3} className="text-center py-[16px] bg-[unset]">
                <NoData />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
